import { err, ok, type Result } from '@shared/result';

/**
 * An email address belonging to one of the company's allowed domains.
 *
 * This type exists to make the corporate-only rule impossible to forget: any
 * function that assigns an area administrator or invites a person takes a
 * `CorporateEmail`, so there is no path that accepts a bare string and skips
 * the check. The handoff calls for exactly this validation when assigning an
 * area administrator ("validación de formato y de dominio").
 *
 * It is *not* the security control. A client-side type cannot gate access —
 * the blocking Cloud Function at sign-in and the Firestore rules do that. This
 * catches typos and gives an immediate, specific error in the form.
 */

export type CorporateEmailError =
  | { readonly kind: 'empty' }
  | { readonly kind: 'malformed'; readonly raw: string }
  | { readonly kind: 'wrong-domain'; readonly raw: string; readonly allowed: readonly string[] };

/**
 * Deliberately conservative rather than RFC-5322 complete: a corporate mailbox
 * is `local@domain.tld` with no quoting or comments. Rejecting an exotic but
 * legal address is a far cheaper mistake here than accepting a malformed one.
 */
const EMAIL_PATTERN = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

export class CorporateEmail {
  private constructor(
    /** Always lower-cased: mailbox comparison here is case-insensitive. */
    readonly value: string,
    readonly domain: string,
  ) {}

  static create(
    raw: string,
    allowedDomains: readonly string[],
  ): Result<CorporateEmail, CorporateEmailError> {
    const normalized = raw.trim().toLowerCase();
    if (normalized.length === 0) return err({ kind: 'empty' });
    if (!EMAIL_PATTERN.test(normalized)) return err({ kind: 'malformed', raw });

    const domain = normalized.slice(normalized.lastIndexOf('@') + 1);
    const allowed = allowedDomains.map((d) => d.trim().toLowerCase().replace(/^@/, ''));

    if (!allowed.includes(domain)) {
      return err({ kind: 'wrong-domain', raw, allowed });
    }

    return ok(new CorporateEmail(normalized, domain));
  }

  /** The part before the `@`. The handoff uses it as the mail preview address. */
  get localPart(): string {
    return this.value.slice(0, this.value.lastIndexOf('@'));
  }

  equals(other: CorporateEmail): boolean {
    return this.value === other.value;
  }

  toString(): string {
    return this.value;
  }

  toJSON(): string {
    return this.value;
  }
}

/**
 * The domain test on its own, for the places that hold a raw string from
 * storage and only need the boolean — a validated-account badge, say.
 */
export const belongsToAllowedDomain = (
  email: string,
  allowedDomains: readonly string[],
): boolean => {
  const normalized = email.trim().toLowerCase();
  if (!EMAIL_PATTERN.test(normalized)) return false;
  const domain = normalized.slice(normalized.lastIndexOf('@') + 1);
  return allowedDomains.some((d) => d.trim().toLowerCase().replace(/^@/, '') === domain);
};
